import type { ReactNode } from "react";
import {
  Accordion, AccordionDetails, AccordionSummary, Button, Checkbox, FormControlLabel, FormGroup, Radio, RadioGroup, Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListIcon from "@mui/icons-material/FilterList";
import { FilterBadge } from "@/components/Events/FilterWidgets.tsx";
import { accordionDetailsStyles, accordionStyles, accordionSummaryStyles, colors } from "@/theme/eventsTokens.ts";
import type { SourcesFilters } from "@/types";
import { firstSeenOptions } from "@/utils/sourcesFilters.ts";
import { applyPreset, isPresetActive, resetPreset, SOURCES_PRESETS } from "@/utils/sourcesPresets.ts";
import type { FilterOption } from "@/utils/sourcesSidebar.ts";
import * as copy from "@/utils/vulnerabilityCopy.ts";

const ICON_SX = { fontSize: 18, color: colors.textMuted } as const;

export function FilterPanel({ title, count, defaultExpanded = false, children }: {
  readonly title: string;
  readonly count: number;
  readonly defaultExpanded?: boolean;
  readonly children: ReactNode;
}) {
  return (
    <Accordion disableGutters defaultExpanded={defaultExpanded} sx={accordionStyles}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummaryStyles}>
        <FilterBadge count={count} icon={<FilterListIcon sx={ICON_SX} />} label={title} />
      </AccordionSummary>
      <AccordionDetails sx={{ ...accordionDetailsStyles, maxHeight: 280, overflowY: "auto" }}>{children}</AccordionDetails>
    </Accordion>
  );
}

export function ChoicePanel({ title, options, selected, onChange }: {
  readonly title: string;
  readonly options: readonly FilterOption[];
  readonly selected: readonly string[];
  readonly onChange: (values: string[]) => void;
}) {
  const toggle = (value: string, checked: boolean) =>
    onChange(checked ? [...selected, value] : selected.filter((other) => other !== value));
  return (
    <FilterPanel title={title} count={selected.length}>
      <FormGroup>
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            label={option.label}
            control={
              <Checkbox size="small" checked={selected.includes(option.value)} onChange={(_event, checked) => toggle(option.value, checked)} />
            }
          />
        ))}
      </FormGroup>
    </FilterPanel>
  );
}

export function FlagPanel({ title, label, checked, disabled, note, onChange }: {
  readonly title: string;
  readonly label: string;
  readonly checked: boolean;
  readonly disabled?: boolean;
  readonly note?: string;
  readonly onChange: (checked: boolean) => void;
}) {
  return (
    <FilterPanel title={title} count={checked ? 1 : 0}>
      <FormControlLabel
        label={label}
        disabled={disabled}
        control={<Checkbox size="small" checked={checked} onChange={(_event, value) => onChange(value)} />}
      />
      {note && <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{note}</Typography>}
    </FilterPanel>
  );
}

export function FirstSeenPanel({ days, windowDays, onChange }: {
  readonly days: number | null;
  readonly windowDays: number | null;
  readonly onChange: (days: number | null) => void;
}) {
  return (
    <FilterPanel title={copy.FILTER_FIRST_SEEN} count={days === null ? 0 : 1}>
      <RadioGroup value={days === null ? "" : String(days)} onChange={(_event, value) => onChange(value === "" ? null : Number(value))}>
        <FormControlLabel value="" control={<Radio size="small" />} label={copy.ANY_TIME} />
        {firstSeenOptions(windowDays).map((option) => (
          <FormControlLabel key={option} value={String(option)} control={<Radio size="small" />}
            label={copy.firstSeenLabel(option, windowDays)} />
        ))}
      </RadioGroup>
    </FilterPanel>
  );
}

export function PresetsPanel({ filters, onChange, yourProductKnown }: {
  readonly filters: SourcesFilters;
  readonly onChange: (filters: SourcesFilters) => void;
  readonly yourProductKnown: boolean;
}) {
  const active = SOURCES_PRESETS.filter((preset) => isPresetActive(filters, preset)).length;
  return (
    <FilterPanel title={copy.PRESETS} count={active} defaultExpanded>
      <Stack spacing={1}>
        {SOURCES_PRESETS.map((preset) => {
          const on = isPresetActive(filters, preset);
          return (
            <Button
              key={preset.label}
              size="small"
              variant={on ? "contained" : "outlined"}
              aria-pressed={on}
              disabled={preset.needsYourProduct && !yourProductKnown}
              onClick={() => onChange(on ? resetPreset(filters, preset) : applyPreset(filters, preset))}
              sx={{ justifyContent: "flex-start", textTransform: "none" }}
            >
              {preset.label}
            </Button>
          );
        })}
      </Stack>
    </FilterPanel>
  );
}
